import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ShieldAlert,
  LayoutDashboard, Monitor, AlertTriangle, FileBarChart,
  Shield, FlaskConical, Users, ChevronRight, Power,
  GitBranch, AlertCircle, Network, Download, MapPin, KeyRound, Building2, Cloud, BookOpen, HardDrive, Activity, FileBadge, Brain,
  Headphones, DollarSign, BarChart2, Puzzle, ShieldCheck, Smartphone, BarChart, Terminal, Truck, Bell,
  Sun, Moon, Rocket, Package, Bot, Radar,
} from 'lucide-react';
import './Layout.css';

// Ícono hexagonal de ITIL Suite
function ItilIcon({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="4"  cy="10" r="3.2" fill="#f97316"/>
      <circle cx="4"  cy="16" r="3.2" fill="#f97316"/>
      <circle cx="4"  cy="22" r="3.2" fill="#f97316"/>
      <circle cx="10" cy="7"  r="2.6" fill="#f97316"/>
      <circle cx="10" cy="13" r="2.6" fill="#f97316"/>
      <circle cx="10" cy="19" r="2.6" fill="#f97316"/>
      <circle cx="10" cy="25" r="2.6" fill="#f97316"/>
      <circle cx="16" cy="5"  r="2.0" fill="#f97316"/>
      <circle cx="16" cy="11" r="2.0" fill="#f97316"/>
      <circle cx="16" cy="17" r="2.0" fill="#f97316"/>
      <circle cx="16" cy="23" r="2.0" fill="#f97316"/>
      <circle cx="16" cy="29" r="2.0" fill="#f97316"/>
      <circle cx="22" cy="8"  r="1.4" fill="#f97316"/>
      <circle cx="22" cy="14" r="1.4" fill="#f97316"/>
      <circle cx="22" cy="20" r="1.4" fill="#f97316"/>
      <circle cx="22" cy="26" r="1.4" fill="#f97316"/>
      <circle cx="28" cy="12" r="1.0" fill="#f97316"/>
      <circle cx="28" cy="18" r="1.0" fill="#f97316"/>
      <circle cx="28" cy="24" r="1.0" fill="#f97316"/>
    </svg>
  );
}

// Navs por rol
const NAV_BASE = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  {
    to: '/assets', label: 'Inventario', icon: Monitor,
    children: [
      { to: '/assets?type=physical',  label: 'Activos físicos',    icon: Monitor,     queryMatch: 'type=physical' },
      { to: '/assets?type=virtual',   label: 'Activos virtuales',  icon: Monitor,     queryMatch: 'type=virtual' },
      { to: '/logical-assets',        label: 'Activos lógicos',    icon: Network },
      { to: '/assets?type=container', label: 'Contenedores',       icon: Monitor,     queryMatch: 'type=container' },
      { to: '/mobiles',               label: 'Móviles',            icon: Smartphone },
      { to: '/software',              label: 'Software',           icon: Package },
      { to: '/providers',             label: 'Proveedores',        icon: Truck },
      { to: '/assets',                label: 'Todos',              icon: Monitor,     exact: true },
    ]
  },
  {
    id: 'operaciones', label: 'Operaciones', icon: Headphones, toggle: true,
    childPaths: ['/incidents', '/knowledge-base', '/changes', '/automation', '/directorio', '/remediation'],
    children: [
      { to: '/incidents',      label: 'Incidentes',    icon: AlertTriangle },
      { to: '/knowledge-base', label: 'Conocimiento',  icon: BookOpen },
      { to: '/changes',        label: 'Cambios',       icon: GitBranch },
      { to: '/automation',     label: 'Automatización', icon: Terminal },
      { to: '/directorio',     label: 'Directorio',    icon: Users },
      { to: '/parches',        label: 'Gestión de Parches', icon: Package },
      { to: '/remediation',    label: 'Remediación (IA)', icon: Bot },
    ]
  },
  {
    id: 'monitoreo', label: 'Monitoreo', icon: Activity, toggle: true,
    childPaths: ['/monitoring/cloud', '/monitoring/onpremise', '/aws-inventory', '/disponibilidad',
                 '/hardware-health', '/network-health'],
    children: [
      { to: '/monitoring/cloud',     label: 'Cloud',           icon: Cloud },
      { to: '/monitoring/onpremise', label: 'On-Premise',      icon: Monitor },
      { to: '/aws-inventory',        label: 'AWS Inventory',   icon: HardDrive },
      { to: '/disponibilidad',       label: 'Disponibilidad',  icon: Moon },
      { to: '/hardware-health',      label: 'Salud del Hardware', icon: Activity },
      { to: '/network-health',       label: 'Salud de red',    icon: Radar },
    ]
  },
  {
    to: '/security', label: 'Seguridad', icon: Shield,
    children: [
      { to: '/security',        label: 'Score de seguridad',  icon: Shield },
      { to: '/hygiene',         label: 'Higiene de software', icon: FlaskConical },
      { to: '/antivirus',       label: 'Postura de Antivirus', icon: ShieldCheck },
      { to: '/anomalies',        label: 'Anomalías',            icon: AlertCircle },
      { to: '/cloud-sync',      label: 'Backup en la nube',   icon: Cloud },
      { to: '/usb-control',     label: 'Control USB',         icon: HardDrive },
      { to: '/policies',        label: 'Políticas',           icon: ShieldCheck },
      { to: '/network-audit',   label: 'Auditoría de Red',    icon: ShieldAlert },
      { to: '/network-discovery', label: 'Descubrimiento de red', icon: Radar },
      { to: '/epm',             label: 'Privilegios (EPM)',   icon: KeyRound },
      { to: '/pam',             label: 'PAM (Sobres)',        icon: KeyRound },
      { to: '/mdm',             label: 'MDM',                 icon: Smartphone },
    ]
  },
  {
    id: 'costos', label: 'Costos', icon: DollarSign, toggle: true,
    childPaths: ['/licenses', '/cloud-costs'],
    children: [
      { to: '/licenses',    label: 'Licencias',    icon: KeyRound },
      { to: '/cloud-costs', label: 'Costos Cloud', icon: Cloud },
    ]
  },
  {
    id: 'informes', label: 'Informes', icon: BarChart2, toggle: true,
    childPaths: ['/reports', '/ai-reports', '/matriz-criticidad'],
    children: [
      { to: '/reports',           label: 'Reportes',          icon: FileBarChart },
      { to: '/ai-reports',        label: 'Informes IA',       icon: Brain },
      { to: '/matriz-criticidad', label: 'Matriz Criticidad', icon: BarChart },
    ]
  },
];

const NAV_USUARIO = [
  { to: '/incidents', label: 'Mis tickets', icon: AlertTriangle },
];

const NAV_RRHH = [
  { to: '/incidents',  label: 'Mis tickets', icon: AlertTriangle },
  { to: '/directorio', label: 'Directorio',  icon: Users },
];

const NAV_ADMIN = NAV_BASE;

const NAV_TECH = NAV_BASE;

const NAV_MANAGER = [
  { to: '/manager', label: 'Panel Gerencial', icon: Building2 },
  { to: '/providers', label: 'Proveedores', icon: Truck },
  {
    id: 'costos', label: 'Costos', icon: DollarSign, toggle: true,
    childPaths: ['/licenses', '/cloud-costs'],
    children: [
      { to: '/licenses',    label: 'Licencias',    icon: KeyRound },
      { to: '/cloud-costs', label: 'Costos Cloud', icon: Cloud },
    ]
  },
  {
    id: 'informes', label: 'Informes', icon: BarChart2, toggle: true,
    childPaths: ['/reports', '/ai-reports'],
    children: [
      { to: '/reports',    label: 'Reportes',    icon: FileBarChart },
      { to: '/ai-reports', label: 'Informes IA', icon: Brain },
    ]
  },
];

// Nav del rol `seguridad`: inventarios, operaciones, seguridad, monitoreo e
// informes (solo seguridad). Sin Costos ni Administración. Se deriva de NAV_BASE
// quitando el grupo Costos y las rutas admin-only /policies y /reports.
const SEG_HIDE_GROUPS = new Set(['costos']);
const SEG_HIDE_PATHS  = new Set(['/policies', '/reports', '/epm', '/pam']);
const NAV_SEGURIDAD = NAV_BASE
  .filter(item => !(item.id && SEG_HIDE_GROUPS.has(item.id)))
  .map(item => item.children
    ? { ...item, children: item.children.filter(c => !SEG_HIDE_PATHS.has(c.to)) }
    : item);

const ROLE_LABEL = {
  admin:      'Administrador',
  technician: 'Soporte',
  manager:    'Gerente',
  viewer:     'Visor',
  seguridad:  'Seguridad',
};

const comingSoon = [
  { label: 'Problemas', icon: AlertCircle },
];

// Verifica si el usuario tiene un rol (principal o extra)
function hasRole(user, role) {
  return user?.role === role || (user?.extra_roles || []).includes(role);
}

// Fusiona items de nav eliminando duplicados por `to`
function mergeNavItems(...navArrays) {
  const seen = new Set();
  return navArrays.flat().filter(item => {
    // Los grupos (toggle) no tienen `to`, se identifican por `id`. Deduplicar por
    // `to || id` evita descartar todos los grupos (que compartían to=undefined).
    const key = item.to || item.id;
    if (key && seen.has(key)) return false;
    if (key) seen.add(key);
    return true;
  });
}

export default function Layout() {
  const { user, logout, features } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasAws, setHasAws] = useState(false);
  const [hasM365, setHasM365] = useState(false);
  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute('data-theme') || localStorage.getItem('theme') || 'light'
  );
  const handleLogout = () => { logout(); navigate('/login'); };
  const closeSidebar = () => setSidebarOpen(false);
  const applyTheme = (next) => {
    document.documentElement.setAttribute('data-theme', next);
    try { localStorage.setItem('theme', next); } catch (e) { /* storage no disponible */ }
    setTheme(next);
  };
  // Sol/Luna alterna claro↔oscuro (si venías de Starlink, vuelve a oscuro).
  const toggleTheme = () => applyTheme(theme === 'dark' ? 'light' : 'dark');
  // Cohete: entra/sale del modo Starlink (monocromo sobre negro).
  const toggleStarlink = () => applyTheme(theme === 'starlink' ? 'dark' : 'starlink');

  // AWS Inventory solo se muestra si hay integración AWS (≥1 cuenta)
  useEffect(() => {
    const base = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${base}/aws/inventory/accounts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setHasAws(Array.isArray(d) && d.length > 0))
      .catch(() => setHasAws(false));
  }, []);

  // Directorio solo se muestra si hay un proveedor de directorio configurado (M365)
  useEffect(() => {
    const base = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${base}/directory/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { configured: false })
      .then(d => setHasM365(!!d.configured))
      .catch(() => setHasM365(false));
  }, []);

  const isManager  = hasRole(user, 'manager');
  const isAdmin    = hasRole(user, 'admin');
  const isPureManager = user?.role === 'manager' && !isAdmin;
  const isUsuario  = user?.role === 'usuario';
  const isSeguridad = user?.role === 'seguridad';

  // Construir nav fusionando todos los roles del usuario
  let navItems;
  if (user?.role === 'rrhh') {
    navItems = NAV_RRHH;
  } else if (isUsuario) {
    navItems = NAV_USUARIO;
  } else if (isSeguridad) {
    navItems = NAV_SEGURIDAD;
  } else if (isPureManager) {
    navItems = NAV_MANAGER;
  } else if (isAdmin) {
    navItems = isManager ? mergeNavItems(NAV_ADMIN, NAV_MANAGER) : NAV_ADMIN;
  } else {
    // technician / viewer con posibles extra_roles
    navItems = isManager ? mergeNavItems(NAV_TECH, NAV_MANAGER) : NAV_TECH;
  }

  // Proveedores: oculto para el rol visor (viewer) — no participa del directorio
  if (user?.role === 'viewer') {
    navItems = navItems.filter(item => item.to !== '/providers');
  }

  // Ocultar "AWS Inventory" si no hay integración AWS (sin mutar las constantes de nav)
  if (!hasAws) {
    navItems = navItems.map(item =>
      item.children?.some(c => c.to === '/aws-inventory')
        ? { ...item, children: item.children.filter(c => c.to !== '/aws-inventory') }
        : item
    );
  }

  // Ocultar "Directorio" si no hay integración de directorio (M365) — item suelto o hijo
  if (!hasM365) {
    navItems = navItems
      .map(item => (item.children ? { ...item, children: item.children.filter(c => c.to !== '/directorio') } : item))
      .filter(item => item.to !== '/directorio');
  }

  // Entitlements por tenant: ocultar módulos no habilitados. Rutas sin mapeo = básico (siempre visibles).
  const FEATURE_BY_PATH = {
    '/incidents': 'incidents', '/knowledge-base': 'incidents', '/changes': 'incidents',
    '/ai-reports': 'ai', '/remediation': 'ai',
    '/automation': 'automation',
    '/mdm': 'mdm',
    '/cloud-costs': 'cloud', '/aws-inventory': 'cloud', '/monitoring/cloud': 'cloud',
    '/disponibilidad': 'disponibilidad',
    '/parches': 'parches',
    '/epm': 'epm',
    '/pam': 'local_admin_backup',
  };
  const featureOk = (to) => {
    const key = FEATURE_BY_PATH[to];
    if (!key) return true;
    return (features || {})[key] === true;
  };
  navItems = navItems
    .map(item => (item.children ? { ...item, children: item.children.filter(c => featureOk(c.to)) } : item))
    .filter(item => (item.children ? item.children.length > 0 : featureOk(item.to)));

  const isInventoryActive = location.pathname.startsWith('/assets') || location.pathname.startsWith('/logical-assets') || location.pathname.startsWith('/mobiles');
  // Salud del Hardware y Salud de red viven en Monitoreo, no acá.
  const isSecurityActive  = location.pathname.startsWith('/security') || location.pathname.startsWith('/hygiene') || location.pathname.startsWith('/antivirus') || location.pathname.startsWith('/usb-control') || location.pathname.startsWith('/cloud-sync') || location.pathname.startsWith('/anomalies') || location.pathname.startsWith('/network-audit') || location.pathname.startsWith('/mdm');

  const isGroupActive = (item) => {
    if (item.to === '/assets')   return isInventoryActive;
    if (item.to === '/security') return isSecurityActive;
    return false;
  };

  // Grupos toggle (sin ruta propia)
  const isToggleGroupActive = (item) =>
    item.childPaths?.some(p => location.pathname.startsWith(p));

  const [openGroups, setOpenGroups] = useState({});

  // Auto-abrir grupos cuando una ruta hija está activa
  useEffect(() => {
    navItems.forEach(item => {
      if (item.toggle && isToggleGroupActive(item)) {
        setOpenGroups(prev => ({ ...prev, [item.id]: true }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (id) => setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="layout">
      {/* Mobile top bar */}
      <div className="mobile-topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src="/nodward-icon.png" alt="Nodward" width={28} height={28} style={{ display: 'block' }} />
          <span style={{ fontSize: 15, letterSpacing: '-0.01em' }}>
            <strong style={{ fontWeight: 800, color: '#1B7FF9' }}>Nod</strong><span style={{ fontWeight: 400, color: 'var(--brand-ink)' }}>ward</span>
          </span>
        </div>
        <button className="hamburger-btn" onClick={() => setSidebarOpen(o => !o)} aria-label="Menú">
          <span /><span /><span />
        </button>
      </div>

      {/* Overlay */}
      <div className={`sidebar-overlay${sidebarOpen ? ' open' : ''}`} onClick={closeSidebar} />

      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="sidebar-brand">
          <div className="brand-icon"><img src="/nodward-icon.png" alt="Nodward" width={26} height={26} style={{ display: 'block' }} /></div>
          <div>
            <div className="brand-name">
              <strong style={{ fontWeight: 800, color: '#1B7FF9' }}>Nod</strong><span style={{ fontWeight: 400, color: 'var(--brand-ink)' }}>ward</span>
            </div>
            <div className="brand-sub">nodward.com</div>
          </div>
        </div>
        <nav className="sidebar-nav">
          <div className="nav-section-label">Módulos</div>
          {navItems.map(item => {
            // Grupos toggle (sin ruta propia: Operaciones, Costos, Informes)
            if (item.toggle) {
              const isActive = isToggleGroupActive(item);
              const isOpen   = openGroups[item.id] || isActive;
              return (
                <div key={item.id}>
                  <button
                    onClick={() => toggleGroup(item.id)}
                    className={`nav-item${isActive ? ' active' : ''}`}
                    style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                  >
                    <item.icon size={16} className="nav-icon-svg" />
                    {item.label}
                    <ChevronRight size={12} style={{ marginLeft: 'auto', transition: 'transform 0.15s', transform: isOpen ? 'rotate(90deg)' : 'none' }} />
                  </button>
                  {isOpen && (
                    <div style={{ marginLeft: 16, borderLeft: '2px solid var(--blue-light)', paddingLeft: 8 }}>
                      {item.children.map(child => (
                        <NavLink key={child.to} to={child.to} end onClick={closeSidebar}
                          className={({ isActive: ca }) => `nav-item nav-subitem${ca ? ' active' : ''}`}>
                          <child.icon size={13} className="nav-icon-svg" />
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            // Grupos con ruta propia (Inventario, Seguridad)
            if (item.children) {
              return (
                <div key={item.to}>
                  <NavLink to={item.to} onClick={closeSidebar} className={() => `nav-item${isGroupActive(item) ? ' active' : ''}`}>
                    <item.icon size={16} className="nav-icon-svg" />
                    {item.label}
                    {isGroupActive(item) && <ChevronRight size={12} style={{ marginLeft: 'auto', transform: 'rotate(90deg)' }} />}
                  </NavLink>
                  {isGroupActive(item) && (
                    <div style={{ marginLeft: 16, borderLeft: '2px solid var(--blue-light)', paddingLeft: 8 }}>
                      {item.children.map(child => {
                        const childActive = child.queryMatch
                          ? location.pathname === '/assets' && location.search.includes(child.queryMatch)
                          : child.exact
                            ? location.pathname === child.to && !location.search
                            : location.pathname === child.to;
                        return (
                          <NavLink key={child.to} to={child.to} end onClick={closeSidebar} className={() => `nav-item nav-subitem${childActive ? ' active' : ''}`}>
                            <child.icon size={13} className="nav-icon-svg" />
                            {child.label}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Items simples
            return (
              <NavLink key={item.to} to={item.to}
                end={item.to === '/' || item.to === '/manager'}
                onClick={closeSidebar}
                className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                <item.icon size={16} className="nav-icon-svg" />
                {item.label}
              </NavLink>
            );
          })}

          {/* Próximamente — solo para admin/tech */}
          {!isManager && (
            <>
              <div className="nav-section-label" style={{ marginTop: 20 }}>Próximamente</div>
              {comingSoon.map(item => (
                <div key={item.label} className="nav-item disabled">
                  <item.icon size={16} className="nav-icon-svg" />
                  {item.label}
                  <span className="nav-soon">pronto</span>
                </div>
              ))}
            </>
          )}

          {/* Admin exclusivo */}
          {user?.role === 'admin' && (
            <>
              <div className="nav-section-label" style={{ marginTop: 20 }}>Administración</div>
              <NavLink to="/baseline" onClick={closeSidebar} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                <ShieldAlert size={16} className="nav-icon-svg" />
                Baseline
              </NavLink>
              <NavLink to="/users" onClick={closeSidebar} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                <Users size={16} className="nav-icon-svg" />
                Usuarios
              </NavLink>
              <NavLink to="/agents" onClick={closeSidebar} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                <Download size={16} className="nav-icon-svg" />
                Agentes
              </NavLink>
              <NavLink to="/locations" onClick={closeSidebar} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                <MapPin size={16} className="nav-icon-svg" />
                Ubicaciones
              </NavLink>
              <NavLink to="/tenants" onClick={closeSidebar} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                <Building2 size={16} className="nav-icon-svg" />
                Tenants
              </NavLink>
              <NavLink to="/audit-reports" onClick={closeSidebar} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                <FileBadge size={16} className="nav-icon-svg" />
                Auditoría
              </NavLink>
              <NavLink to="/integrations" onClick={closeSidebar} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                <Puzzle size={16} className="nav-icon-svg" />
                Integraciones
              </NavLink>
              <NavLink to="/notifications" onClick={closeSidebar} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                <Bell size={16} className="nav-icon-svg" />
                Notificaciones
              </NavLink>
              <NavLink to="/approvers" onClick={closeSidebar} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                <ShieldCheck size={16} className="nav-icon-svg" />
                Aprobadores
              </NavLink>
              <NavLink to="/audit-log" onClick={closeSidebar} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                <Activity size={16} className="nav-icon-svg" />
                Registro de actividad
              </NavLink>
            </>
          )}
        </nav>
        <div className="sidebar-footer">
          <NavLink to="/profile" onClick={closeSidebar} style={{ textDecoration: 'none', flex: 1, minWidth: 0 }}>
            <div className="user-info" style={{ cursor: 'pointer' }} title="Mi perfil · 2FA">
              <div className="user-avatar">{user?.full_name?.[0] || 'U'}</div>
              <div style={{ minWidth: 0 }}>
                <div className="user-name">{user?.full_name}</div>
                <div className="user-role">{ROLE_LABEL[user?.role] || user?.role}</div>
              </div>
            </div>
          </NavLink>
          <button
            className="theme-toggle-btn"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
            aria-label={theme === 'dark' ? 'Activar modo claro' : 'Activar modo oscuro'}
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            className={`theme-toggle-btn${theme === 'starlink' ? ' theme-toggle-btn--on' : ''}`}
            onClick={toggleStarlink}
            title={theme === 'starlink' ? 'Salir del modo Starlink' : 'Modo Starlink'}
            aria-label={theme === 'starlink' ? 'Desactivar modo Starlink' : 'Activar modo Starlink'}
          >
            <Rocket size={16} />
          </button>
          <button className="logout-btn" onClick={handleLogout} title="Cerrar sesión">
            <Power size={16} />
          </button>
        </div>
      </aside>
      <main className="main-content"><Outlet /></main>
    </div>
  );
}
